import { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import type { OccurrenceWithEvent } from '@fellowship/shared'
import { EventBadge } from './ui/EventBadge'
import { AvatarStack } from './ui/Avatar'
import { LogoTile } from './ui/LogoTile'
import { EVENT_TYPES, EVENT_TYPE_IMAGES, GRADIENT } from './ui/eventTypes'
import { apiFetch } from '../lib/api'
import { getSessionId } from '../lib/sessionId'

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}

export function formatDistance(meters: number) {
  const miles = meters / 1609.34
  return miles < 10 ? `${miles.toFixed(1)} mi` : `${Math.round(miles)} mi`
}

interface Props {
  occurrence: OccurrenceWithEvent
  selected?: boolean
  onClick?: () => void
  index?: number
  trackImpression?: boolean
}

export default function OccurrenceCard({ occurrence, selected, onClick, index, trackImpression }: Props) {
  const { event } = occurrence
  const cardRef = useRef<HTMLElement>(null)
  const impressionFired = useRef(false)

  useEffect(() => {
    if (!trackImpression || impressionFired.current) return
    const el = cardRef.current
    if (!el) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !impressionFired.current) {
          impressionFired.current = true
          observer.disconnect()
          apiFetch(`/events/${event.id}/metrics`, {
            method: 'POST',
            body: JSON.stringify({ metricType: 'impression', sessionId: getSessionId() }),
          }).catch(() => {})
        }
      },
      { threshold: 0.5 },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [trackImpression, event.id])
  const eventType = event.eventType ?? 'potluck'
  const t = EVENT_TYPES[eventType as keyof typeof EVENT_TYPES] ?? EVENT_TYPES.potluck
  const isScraped = event.source === 'scraped'
  const isFillingUp =
    !isScraped &&
    occurrence.rsvpCount !== undefined &&
    event.maxAttendees !== undefined &&
    event.maxAttendees !== null &&
    event.maxAttendees > 0 &&
    occurrence.rsvpCount / event.maxAttendees >= 0.75
  const church = event.church
  const accent = church?.branding?.accentColor

  return (
    <article
      ref={cardRef}
      onClick={onClick}
      data-occurrence-id={occurrence.id}
      style={{
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 16,
        overflow: 'hidden',
        flexShrink: 0,
        cursor: 'pointer',
        background: selected ? t.tint : 'var(--surface)',
        border: `${selected ? 2 : 1}px solid ${selected ? t.color : 'var(--line-soft)'}`,
        margin: selected ? '-1px 0' : 0,
        transition: 'background 0.15s ease, border-color 0.15s ease',
      }}
    >
      <div style={{ display: 'flex', gap: 14, padding: '14px 16px' }}>
      <div
        style={{
          width: 95,
          height: 95,
          flexShrink: 0,
          borderRadius: 10,
          overflow: 'hidden',
          background: t.tint,
        }}
      >
        <img
          src={event.imageUrl ?? EVENT_TYPE_IMAGES[eventType] ?? EVENT_TYPE_IMAGES.potluck}
          alt=""
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          onError={(e) => {
            ;(e.currentTarget as HTMLImageElement).src =
              EVENT_TYPE_IMAGES[eventType] ?? EVENT_TYPE_IMAGES.potluck
          }}
        />
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            marginBottom: 5,
            flexWrap: 'wrap',
          }}
        >
          <EventBadge type={eventType} index={index} />
          {isScraped && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                height: 20,
                padding: '0 8px',
                borderRadius: 999,
                background: 'var(--surface-2)',
                border: '1px solid var(--line-soft)',
                fontSize: 11,
                color: 'var(--muted)',
                fontWeight: 500,
              }}
            >
              Unverified
            </span>
          )}
          {isFillingUp && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                height: 20,
                padding: '0 8px',
                borderRadius: 999,
                background: '#FEF3C7',
                border: '1px solid #FCD34D',
                fontSize: 11,
                color: '#92400E',
                fontWeight: 600,
              }}
            >
              Filling up
            </span>
          )}
          {occurrence.distanceMeters !== undefined && (
            <span style={{ marginLeft: 'auto', fontSize: 11.5, color: 'var(--muted)', fontWeight: 500 }}>
              {formatDistance(occurrence.distanceMeters)}
            </span>
          )}
        </div>

        <div
          style={{
            fontSize: 15,
            fontWeight: 600,
            color: 'var(--ink)',
            lineHeight: 1.2,
            marginBottom: 4,
          }}
        >
          <Link
            to={`/events/${event.id}`}
            onClick={(e) => e.stopPropagation()}
            style={{ color: 'inherit', textDecoration: 'none' }}
          >
            {event.title}
          </Link>
        </div>

        <div style={{ display: 'flex', gap: 12, fontSize: 12, color: 'var(--ink-2)', marginBottom: 5 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            {formatDate(occurrence.startsAt)}
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            {formatTime(occurrence.startsAt)}
          </span>
        </div>

        {!accent && (
          <div
            style={{
              fontSize: 11.5,
              color: 'var(--muted)',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              marginBottom: 5,
            }}
          >
            {event.hostedByType === 'person'
              ? 'Person-hosted'
              : event.locationName ?? 'Church event'}
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {occurrence.rsvpCount !== undefined && !isScraped && (
              <>
                <AvatarStack names={[]} size={20} max={3} />
                <span style={{ fontSize: 11, color: 'var(--ink-2)' }}>{occurrence.rsvpCount} going</span>
              </>
            )}
          </div>
          <Link to={`/events/${event.id}`} onClick={(e) => e.stopPropagation()}>
            <button
              style={{
                height: 28,
                padding: '0 12px',
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                fontFamily: 'inherit',
                background: selected ? GRADIENT : 'transparent',
                color: selected ? '#fff' : 'var(--primary)',
                border: selected ? 'none' : `1.5px solid var(--primary)`,
              }}
            >
              View <ArrowRight size={11} />
            </button>
          </Link>
        </div>
      </div>
      </div>

      {accent && church && (
        <div
          style={{
            padding: '7px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            background: `color-mix(in srgb, ${accent} 8%, transparent)`,
            borderTop: `1px solid color-mix(in srgb, ${accent} 14%, transparent)`,
          }}
        >
          <LogoTile logoUrl={church.branding?.logoUrl} name={church.name} accentColor={accent} size={18} />
          <Link
            to={`/churches/${church.slug}`}
            onClick={(e) => e.stopPropagation()}
            style={{ fontSize: 11.5, fontWeight: 600, color: accent, textDecoration: 'none' }}
          >
            {church.name}
          </Link>
          <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--muted)' }}>
            {church.followerCount} {church.followerCount === 1 ? 'follower' : 'followers'}
          </span>
        </div>
      )}
    </article>
  )
}
